import {
  ArrowRight,
  Box,
  CheckCircle2,
  FileBox,
  GitCompare,
  Languages,
  Lock,
  Ruler,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ProductShot } from "../components/ProductShot";
import { useI18n } from "../i18n";
import { useDesktopReleases } from "../lib/desktopReleases";
import { DownloadSection } from "./home/DownloadSection";

// three.js is loaded only with the 3D block, not with the page.
const ModelSpin = lazy(() => import("../components/ModelSpin"));

import { HomeHero } from "./home/HomeHero";

export const FEATURES = [
  { icon: Box, key: "modeling" },
  { icon: Ruler, key: "sketches" },
  { icon: GitCompare, key: "history" },
  { icon: FileBox, key: "formats" },
  { icon: Sparkles, key: "ai" },
  { icon: Languages, key: "ui" },
] as const;

const STEPS = [
  { key: "sketch", scene: "sketch-constraints-focus", ratio: [1120, 884] },
  { key: "ai", scene: "ai-candidate-review-focus", ratio: [1120, 884] },
  { key: "params", scene: "model-editor-focus", ratio: [1120, 884] },
  { key: "history", scene: "project-history", ratio: [1600, 960] },
] as const;

const STATS = ["price", "disk", "ram", "lang"] as const;
const FAQ = ["free", "offline", "formats"] as const;

/** Mounts children (and so loads their code) only when the block comes close to the screen. */
function NearViewport({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) setNear(true);
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return (
    <div ref={ref}>{near ? children : <div className="model-spin" />}</div>
  );
}

export default function Home() {
  const { t, locale } = useI18n();
  const releases = useDesktopReleases();
  const latest = releases.data?.releases[0];
  const size = latest?.assets.find(
    (a) => a.kind === "nsis-installer",
  )?.sizeBytes;
  const sizeMb = size
    ? new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
        size / 1024 / 1024,
      )
    : undefined;
  return (
    <>
      <HomeHero sizeMb={sizeMb} />
      <div className="container home-hero-shot home-hero-product-shot">
        <ProductShot scene="workspace-hero" video="workspace-orbit" alt={t("home.heroAlt")} priority />
      </div>

      <section className="home-stats" aria-label={t("home.statsLabel")}>
        <div className="container home-stats-grid">
          {STATS.map((k) => (
            <div key={k} className="home-stat">
              <strong>{t(`home.stat.${k}.value`)}</strong>
              <span>{t(`home.stat.${k}.label`)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="how">
        <div className="container">
          <header className="home-section-head">
            <span className="eyebrow">{t("home.howEyebrow")}</span>
            <h2>{t("home.howTitle")}</h2>
            <p className="muted">{t("home.howLead")}</p>
          </header>
          <div className="home-steps">
            {STEPS.map(({ key, scene, ratio }, i) => (
              <article key={key} className={`home-step ${i % 2 ? "flip" : ""}`}>
                <div className="home-step-copy">
                  <h3>{t(`home.step.${key}.title`)}</h3>
                  <p>{t(`home.step.${key}.text`)}</p>
                  <ul>
                    {[1, 2].map((n) => (
                      <li key={n}>
                        <CheckCircle2 size={15} aria-hidden />{" "}
                        {t(`home.step.${key}.point${n}`)}
                      </li>
                    ))}
                  </ul>
                </div>
                <ProductShot
                  scene={scene}
                  width={ratio[0]}
                  height={ratio[1]}
                  alt={t(`home.step.${key}.alt`)}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container stack" style={{ gap: 28 }}>
          <header className="home-section-head">
            <span className="eyebrow">{t("home.featuresEyebrow")}</span>
            <h2>{t("home.featuresTitle")}</h2>
          </header>
          <div className="home-features">
            <div className="home-features-grid">
              {FEATURES.map(({ icon: Icon, key }) => (
                <article className="card home-feature" key={key}>
                  <div className="feature-icon">
                    <Icon size={19} aria-hidden />
                  </div>
                  <h3>{t(`features.${key}.title`)}</h3>
                  <p className="muted" style={{ marginTop: 6 }}>
                    {t(`features.${key}.text`)}
                  </p>
                </article>
              ))}
            </div>
            <NearViewport>
              <Suspense fallback={<div className="model-spin" />}>
                <ModelSpin src="/models/gear.glb" label={t("home.modelAlt")} />
              </Suspense>
            </NearViewport>
          </div>
          <div>
            <Link className="button" to="/features">
              {t("home.ctaFeatures")} <ArrowRight size={15} aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container home-privacy">
          <div className="stack">
            <span className="eyebrow">
              <Lock size={13} aria-hidden /> {t("home.privacyEyebrow")}
            </span>
            <h2>{t("home.privacyTitle")}</h2>
            <p className="muted">{t("home.accountText")}</p>
          </div>
          <ul className="home-promises">
            {[1, 2, 3].map((n) => (
              <li key={n}>
                <ShieldCheck size={18} aria-hidden />{" "}
                <span>{t(`home.promise${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container home-faq">
          <header className="home-section-head">
            <span className="eyebrow">{t("home.faqEyebrow")}</span>
            <h2>{t("home.faqTitle")}</h2>
          </header>
          <div className="home-faq-grid">
            {FAQ.map((k) => (
              <article key={k} className="card">
                <h3>{t(`home.faq.${k}.q`)}</h3>
                <p className="muted">{t(`home.faq.${k}.a`)}</p>
              </article>
            ))}
          </div>
          <Link to="/faq">
            {t("home.faqAll")} <ArrowRight size={14} aria-hidden />
          </Link>
        </div>
      </section>

      <DownloadSection sizeMb={sizeMb} />
    </>
  );
}
